import { type ReactElement, type ReactNode } from 'react';

import { SignIn } from '@/auth/SignIn';
import { useSession } from '@/auth/useSession';

/**
 * The gate in front of the app shell.
 *
 * The loading state matters: showing sign-in while the session is still
 * resolving flashes a false negative at someone who is, in fact, signed in.
 */
export function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const { status, error, refresh } = useSession();

  if (status === 'loading') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-ugin-bg">
        <p role="status" className="text-sm text-ugin-text-muted">
          Loading…
        </p>
      </main>
    );
  }

  if (status === 'error') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-ugin-bg px-4">
        <div className="w-full max-w-sm space-y-3 text-center text-sm">
          <p className="text-ugin-text">
            {error?.message ?? 'Could not reach Ugin.'}
          </p>
          <button
            type="button"
            className="min-h-11 w-full rounded-md border border-ugin-border px-4 py-2 text-ugin-text"
            onClick={() => void refresh()}
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (status === 'anonymous') return <SignIn />;

  return <>{children}</>;
}
